import { formatMoney } from '@shopify/theme-currency';

class ConfiguratorComponent extends HTMLElement {
  constructor() {
    super();

    // Get the main element
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.cartNotification = document.querySelector('cart-notification');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.steps = this.querySelectorAll('[data-step]');
    this.progressBar = this.querySelector('[data-progress-bar]');

    // Get the product price elements
    this.price = this.querySelector('[data-price]');
    this.compareAtPrice = this.querySelector('[data-compare-at-price]');
    this.priceLoader = this.querySelector('[data-price-loader]');
    this.priceLabel = this.querySelector('[data-price-label]');

    // Get the general error message elements
    this.generalErrorMessage = this.querySelector('[data-general-error-message]');
    this.generalErrorMessageContent = this.querySelector('[data-general-error-message-content]');
    this.generalErrorMessageText = this.querySelector('[data-general-error-message-text]');
    this.generalErrorMessageClose = this.querySelector('[data-general-error-message-close]');

    // Options
    this.options = {
      debug: false,
    };

    // Get product data
    this.productData = this.getProductData();
    if (this.options.debug) console.log('productData', this.productData);

    // Get translations
    this.translations = parseJson(this.dataset.translations);

    // Event listeners
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('keypress', this.onEnterPress.bind(this));
    this.form.querySelectorAll('input, select, textarea').forEach((input) => {
      input.addEventListener('keyup', this.onKeyUp.bind(this));
      input.addEventListener('change', this.onChange.bind(this));
    });
    this.generalErrorMessageClose.addEventListener('click', () => {
      this.closeGeneralErrorMessage();
    });
  }

  /**
   * Listen for enter press on form
   * @param {Event} event
   */
  async onEnterPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  /**
   * Listen for input key up
   * @param {Event} event
   */
  async onKeyUp(event) {
    // remove error state from input
    this.removeError(event.currentTarget ?? event.target);
  }

  /**
   * Listen for form changes
   * @param {Event} event
   */
  async onChange(event) {
    // remove error state from input
    this.removeError(event.currentTarget ?? event.target);

    // Get form data as object
    this.formData = Object.fromEntries(new FormData(this.form));
    if (this.options.debug) console.log('formData', this.formData);

    // Calculate curtain price
    const calculation = this.priceCalculation();
    if (this.options.debug) console.log('calculation', calculation);

    // Update the price element (with a error the min price will be used)
    this.updatePrice(calculation.line_price, calculation.line_compare_at_price);

    // Handle error messages
    if (calculation?.errors) {
      this.handleErrors(calculation.errors);
    }

    // Handle next steps
    else {
      this.handleSteps(event.currentTarget ?? event.target);
    }
  }

  /**
   * Listen for form submit
   * @param {Event} event
   */
  onSubmit(event) {
    event.preventDefault();

    // Set in processing state
    this.toggleLoading(true);

    // Get form data as object
    this.formData = Object.fromEntries(new FormData(this.form));
    if (this.options.debug) console.log('formData', this.formData);

    // Calculate curtain price
    const calculation = this.priceCalculation();
    if (this.options.debug) console.log('calculation', calculation);

    // Update the price element (should be already done by onChange event)
    this.updatePrice(calculation.line_price, calculation.line_compare_at_price);

    // Format add to cart item
    const lineItem = this.formatLineItem(calculation);
    if (this.options.debug) console.log('item', item);

    // Add to cart
    this.addToCart(lineItem);
  }

  /**
   * @returns current cart data
   */
  async getCartData() {
    const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
      ...fetchConfig('json', 'GET'),
    });
    const cartData = await cartResponse.json();
    return cartData;
  }

  /**
   * @returns product data
   */
  getProductData() {
    const productData = this.querySelector('[data-product-json]')
      ? parseJson(this.querySelector('[data-product-json]').textContent)
      : {};

    // Parse numbers from product data
    [
      'min_width',
      'max_width',
      'min_height',
      'max_height',
      'max_square_centimeter',
      'max_square_meter',
      'price',
      'compare_at_price',
      'from_price',
      'from_compare_at_price',
      'lane_width_in_cm',
    ].map((key) => {
      if (!productData[key]) return;
      productData[key] = parseFloat(productData[key]);
    });

    // Return product data
    return productData;
  }

  /**
   * Calculate curtain price
   * @returns {object} calculation
   */
  priceCalculation() {
    return {
      price: 14945,
      line_price: 14945,
      compare_at_price: 16445,
      line_compare_at_price: 16445,
      token: '34754569861494491308',
    };
  }

  /**
   * Add to cart
   * @param {object} lineItem
   * @returns {void}
   */
  async addToCart(lineItem) {
    try {
      // Add to cart body
      const body = JSON.stringify({
        items: [lineItem],
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname,
      });

      // Add to cart request
      const addToCartResponse = await fetch(`${routes.cart_add_url}`, {
        ...fetchConfig('json', 'POST'),
        ...{ body },
      });
      const addToCartData = await addToCartResponse.json();
      if (this.options.debug) console.log('addToCartData', addToCartData);

      // Get product data
      if (!addToCartData.items) {
        throw new Error(
          'You ordered the max amount of items, please contact us for more information or place a second order'
        );
      }

      // Get product data
      const productData = addToCartData.items[0];

      // Show notification
      this.showNotification(addToCartData);

      // End Processing state
      this.toggleLoading(false);

      // Push to Shopify analytics pixel
      if (Shopify?.analytics) {
        const dataLayer = window.dataLayer || [];

        // Push to dataLayer
        dataLayer.push({
          event: 'custom_product_added_to_cart',
          price: productData.price,
          currency: Shopify?.currency?.active || 'EUR',
          product_title: productData.product_title,
          product_type: productData.properties._product_type,
          quantity: productData.quantity,
          total_cost: productData.total_price,
        });

        // Push to Shopify analytics
        Shopify.analytics.publish('custom_product_added_to_cart', {
          price: productData.price,
          currency: Shopify?.currency?.active || 'EUR',
          product_title: productData.product_title,
          product_type: productData.properties._product_type,
          quantity: productData.quantity,
          total_cost: productData.total_price,
        });
      }
    } catch (error) {
      console.error(error);

      // Show notification
      this.showNotification({
        errors: error.message,
      });

      // End Processing state
      this.toggleLoading(false);
    }
  }

  /**
   * Translate keys and values for displayed line attribues from translations
   * @param {string} input = the key to translate that match this.translations
   * @returns {string} value
   */
  translateKeyValue(input) {
    let value = input.replaceAll('_', ' ').replaceAll('-', ' ');
    value = value.charAt(0).toUpperCase() + value.slice(1);
    let key = input.replaceAll(' ', '_').replaceAll('-', '_').toLowerCase();
    if (this.translations[key]) {
      value = this.translations[key];
    }
    return value;
  }

  /**
   * Toggle loading state of submit button
   * @param {int} price - The price of the product
   * @param {int} compareAtPrice - The compare at price of the product
   * @returns {void}
   */
  updatePrice(price, compareAtPrice) {
    if (!this.price || !this.compareAtPrice || !this.priceLoader) return;
    this.priceLoader.classList.remove('hidden');
    this.priceLabel.classList.add('hidden');
    this.price.classList.add('hidden');
    this.compareAtPrice.classList.add('hidden');

    setTimeout(() => {
      this.price.textContent = formatMoney(price, window.moneyFormat);
      this.compareAtPrice.textContent = formatMoney(compareAtPrice, window.moneyFormat);
      this.priceLoader.classList.add('hidden');
      this.priceLabel.classList.remove('hidden');
      this.price.classList.remove('hidden');
      this.compareAtPrice.classList.toggle('hidden', price == compareAtPrice);
    }, 300);
  }

  /**
   * Remove error state from input
   * @param {HTMLElement} input
   */
  removeError(input) {
    if (!input) return;
    input.removeAttribute('aria-invalid');
    input.classList.remove('error');
  }

  /**
   * close general error message
   */
  closeGeneralErrorMessage() {
    this.generalErrorMessage.classList.add('pointer-events-none');
    this.generalErrorMessage.classList.add('overflow-hidden');
    this.generalErrorMessage.classList.add('opacity-0');
    this.generalErrorMessage.style.maxHeight = '0rem';
  }

  /**
   * Handle errors from price calculation
   * @param {Array} errors
   */
  handleErrors(errors) {
    if (!errors) return;

    errors.map((error) => {
      const message = this.translations[error.handle]
        ? this.translations[error.handle]
        : error.message;

      switch (error.type) {
        case 'general':
          this.generalErrorMessageText.textContent = message;
          this.generalErrorMessage.classList.remove('pointer-events-none');
          this.generalErrorMessage.classList.remove('overflow-hidden');
          this.generalErrorMessage.classList.remove('opacity-0');
          this.generalErrorMessage.style.maxHeight = '20rem';
          break;
      }
    });
  }

  /**
   * Set next step
   * @param {string} step that matches data-step attribute
   */
  setNextStep(step) {
    if (!step) return;
    this.state.step = step;
    this.steps.forEach((el) => {
      if (el.dataset.step == step) {
        el.classList.remove('pointer-events-none');
        el.classList.remove('overflow-hidden');
        el.classList.remove('opacity-0');
        el.style.maxHeight = '20rem';
      }
    });
    // If next step is location then show also the quantity step
    if (step == 'location') {
      this.setNextStep('quantity');
    }
  }

  /**
   * Set prev step
   * @param {string} step that matches data-step attribute
   */
  setPrevStep(step) {
    if (!step) return;
    this.state.step = step;
    this.steps.forEach((el) => {
      if (el.dataset.step == step) {
        el.classList.add('pointer-events-none');
        el.classList.add('overflow-hidden');
        el.classList.add('opacity-0');
        el.style.maxHeight = '0rem';
      }
    });
  }

  /**
   * Set progress bar step
   */
  setProgressBar(step) {
    if (!step) return;
    this.state.progressBar = step;
    const index = this.options.progressBarSteps.indexOf(step) + 1;
    const progress = (index / this.options.progressBarSteps.length) * 100;
    this.progressBar.style.width = `${progress}%`;
  }

  /**
   * Toggle submit button
   * @param {boolean} active
   */
  toggleSubmitButton(active = true) {
    if (active) {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.innerHTML = this.translations.submit_button_text;
      this.submitButton.setAttribute('aria-label', this.translations.submit_button_text);
    } else {
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.innerHTML = this.translations.submit_button_text_disabled;
      this.submitButton.setAttribute('aria-label', this.translations.submit_button_text_disabled);
    }
  }

  /**
   * Toggle loading state of submit button
   * @param {*} active: boolean
   */
  toggleLoading(active = true) {
    if (!this.submitButton) return;
    this.submitButton.toggleAttribute('disabled', active);
    this.submitButton.classList.toggle('button--loading', active);
  }

  /**
   * Show add to cart notification to user
   * @param {object} state : add to cart response
   * @returns
   */
  showNotification(state, inventory_quantity) {
    if (!state) return;

    // Show error message if something went wrong
    if (state.errors) {
      this.cartNotification.setActiveElement(document.activeElement);
      if (state.errors.includes('quantity')) {
        this.cartNotification.renderError(state.errors, inventory_quantity);
      } else {
        this.cartNotification.renderError(state.errors);
      }
      return;
    }

    // Update cart drawer data
    this.cartDrawer && this.cartDrawer.renderContents(state);

    // Show notification as drawer or notification
    switch (this.submitButton.dataset.addToCartBehavior) {
      case 'open_cart_drawer':
        // Set timeout to force animation. Because content is just updated with renderContents
        setTimeout(() => {
          this.cartDrawer && this.cartDrawer.open();
        });
        break;
      case 'show_cart_notification':
        this.cartNotification.setActiveElement(document.activeElement);
        this.cartNotification.renderContents(state);
    }
  }

  /**
   * @returns Sections to render after add to catt action
   */
  getSectionsToRender() {
    let sections = [...this.cartNotification.getSectionsToRender().map((section) => section.id)];
    if (this.cartDrawer) {
      sections = [
        ...sections,
        ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
      ];
    }
    return sections;
  }
}

export default ConfiguratorComponent;
